import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { NAV_LINKS } from '../content/siteContent';
import { scrollToId } from '../lib/scrollTo';

export default function Navbar() {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      setHidden(y > lastY && y > 200);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      window.lenis?.stop();
      document.body.style.overflow = 'hidden';
    } else {
      window.lenis?.start();
      document.body.style.overflow = '';
    }
  }, [open]);

  const go = (target) => {
    setOpen(false);
    setTimeout(() => scrollToId(target), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: hidden ? '-100%' : 0 }}
        transition={{
          opacity: { delay: 1.9, duration: 0.8 },
          y: { duration: 0.45, ease: [0.33, 1, 0.68, 1] },
        }}
        className={`fixed inset-x-0 top-0 z-[100] transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? 'border-b border-white/10 bg-black/60 backdrop-blur-xl' : 'border-b border-transparent'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 md:px-12 md:py-5">
          <button
            data-testid="nav-logo"
            onClick={() => go('opening')}
            className="flex items-center gap-2.5"
          >
            <img src="/logo-icon.png" alt="Amaya Energy logo" className="h-7 w-7" />
            <span className="font-display text-sm font-bold tracking-[0.12em] text-white">Amaya Energy</span>
          </button>

          <nav className="hidden items-center gap-8 md:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.target}
                data-testid={`nav-link-${l.target}`}
                onClick={() => go(l.target)}
                className="group relative text-[11px] font-medium uppercase tracking-[0.22em] text-white/60 transition-colors duration-300 hover:text-white"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#47a0d9] transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
            <button
              data-testid="nav-cta"
              onClick={() => go('contact')}
              className="flex items-center gap-1.5 border border-white/25 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-[#47a0d9] hover:bg-[#47a0d9] hover:text-black"
            >
              Partner <ArrowUpRight size={13} />
            </button>
          </nav>

          <button
            data-testid="mobile-menu-open"
            onClick={() => setOpen(true)}
            className="text-white md:hidden"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[150] flex flex-col bg-[#000000]"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="flex items-center gap-2.5">
                <img src="/logo-icon.png" alt="" className="h-7 w-7" />
                <span className="font-display text-sm font-bold tracking-[0.12em] text-white">Amaya Energy</span>
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-white"
                aria-label="Close menu"
              >
                <X size={26} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.target}
                  data-testid={`mobile-nav-${l.target}`}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
                  onClick={() => go(l.target)}
                  className="text-left font-display text-4xl font-semibold uppercase tracking-tight text-white/80 transition-colors duration-300 hover:text-[#47a0d9]"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="px-8 pb-10 text-[11px] uppercase tracking-[0.25em] text-white/40"
            >
              Energy in motion
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
